import * as React from 'react';
import * as R from 'ramda';
import * as moment from 'moment';
import { Report9BoxDataService } from '../../../services';
import { PVATeamSelect, PVAContentHeader, PVAContentBody, LoadingIndicatorMini, PVABadge, PonderaModal } from '../../../components';
import { User, Team, Report9BoxSettings, Report9BoxText, UserStyle } from '../../../models';
import { BaseRouteProps } from '../../../interfaces';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { UserStore, FilterStore } from '../../../stores';
import './9box.css';
import { NavLink } from 'react-router-dom';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { PVAGenericSelect } from '../blocks';
import { Report9BoxSnapshot } from '../../../models/Report9BoxSnapshot';

interface BoxReport {
    name: string;
    color: string;
    users: User[];
    box: number;
    show: boolean;
}

interface Report9BoxEditState {
    previousUsers: User[];
    reported: BoxReport[];
    filter: string;
    miniLoad: boolean;
    teamId: number;
    orgId: number;
    reportSettings: Report9BoxSettings | undefined;
    small: boolean;
    listView: boolean;
    open: boolean;
    selectedSnapshotId: number;
    snapshots: Report9BoxSnapshot[];
    currentlyViewingPastSnapshot: boolean;
}

interface Report9BoxProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class Report9BoxView extends React.Component<Report9BoxProps, Report9BoxEditState> {
    // private userDataService = new UserDataService(this.props.notificationService);
    private report9BoxDataService = new Report9BoxDataService(this.props.notificationService);
    private defaultKeyValues: any;
    private defaultColors: any;

    constructor(props: Report9BoxProps) {
        super(props);

        this.defaultKeyValues = {
            box1: 'Consistent Star',
            box2: 'Current Star',
            box3: 'Future Star',
            box4: 'Expert Individual Performer',
            box5: 'Key Player',
            box6: 'Diamond in the Rough',
            box7: 'Reliable Employee',
            box8: 'New Player',
            box9: 'Talent Risk',
            box1Desc: '<ul><li>Very Strong Performer</li><li>Is or has potential to move up to a senior leadership position</li></ul>',
            box2Desc: '<ul><li>Valuable contributor to the business</li><li>Can play a significant role at higher position in the organization</li></ul>',
            box3Desc: '<ul><li>Solid performer with room to grow in current assignment</li><li>Is or has potential to move up to a senior leadership position</li></ul>',
            box4Desc: '<ul><li>Seasoned professional (difficult to replace)</li><li>Not promotable yet very valuable</li> <li>May be called upon to assist with other opportunities</li></ul>',
            box5Desc: '<ul><li>Good Performer with room to grow at current level</li><li>May be promotable in the future</li></ul>',
            box6Desc: '<ul><li>Exceeded expectations in his/her last job yet recently promoted</li><li>Requires some time and experience coupled with coaching to reach full performance</li><li>Is or has potential to move up to a senior leadership position</li></ul>',
            box7Desc: '<ul><li>Competent yet currently not promotable</li></ul>',
            box8Desc: '<ul><li>Person has ability to develop skills</li><li>New to position</li><li>May be promotable in the future</li></ul>',
            box9Desc: '<ul><li>Performing below expectations</li><li>Requires action- fix or replace</li></ul>',
            xScale1: 'Room to Develop/Improve',
            xScale2: 'Solid Performers',
            xScale3: 'Exceptional Performers',
            xArrow: 'Performance',
            yScale1: 'Highly Promotable',
            yScale2: 'Promotable',
            yScale3: 'Not Promotable',
            yArrow: 'Promotability'
        };

        this.defaultColors = {
            box1Color: 'rgba(135, 214, 214, 0.7)',
            box2Color: 'rgba(135, 214, 214, 0.7)',
            box3Color: 'rgba(135, 214, 214, 0.7)',
            box4Color: 'rgba(222, 174, 255, 0.7)',
            box5Color: 'rgba(184, 227, 168, 0.7)',
            box6Color: 'rgba(184, 227, 168, 0.7)',
            box7Color: 'rgba(254, 209, 129, 0.7)',
            box8Color: 'rgba(254, 209, 129, 0.7)',
            box9Color: 'rgba(249, 174, 174, 0.7)'
        };

        var boxNames: string[] = [
            'Diamond in the Rough', 'Future Star', 'Consistent Star',
            'New Player', 'Key Player', 'Current Star',
            'Talent Risk', 'Reliable Employee', 'Expert Individual Performer'
        ];
        var colors: string[] = [
            'green', 'blue', 'blue',
            'yellow', 'green', 'blue',
            'red', 'yellow', 'green'
        ];
        var boxOrder: number[] = [
            6, 3, 1,
            8, 5, 2,
            9, 7, 4
        ];
        var reported: BoxReport[] = [];
        for (var i = 0; i < 9; i++) {
            reported.push({
                name: boxNames[i],
                users: [],
                color: colors[i],
                box: boxOrder[i],
                show: false
            });
        }
        this.state = {
            previousUsers: [],
            reported: reported,
            filter: '',
            teamId: 0,
            miniLoad: false,
            orgId: 0,
            reportSettings: undefined,
            small: false,
            listView: false,
            open: false,
            selectedSnapshotId: 0,
            snapshots: [],
            currentlyViewingPastSnapshot: false
        };
    }

    @computed get users() {
        return this.props.FilterStore!.users;
    }

    @computed get teamId() {
        return this.props.FilterStore!.teamId;
    }

    @computed get organizationId() {
        return this.props.FilterStore!.organizationId;
    }

    @computed get team() {
        if (this.props.FilterStore && this.props.FilterStore.team) {
            return this.props.FilterStore.team;
        }
        return null;
    }

    @computed get teams() {
        if (this.props.FilterStore && this.props.FilterStore.teams) {
            return this.props.FilterStore.teams;
        }
        return [];
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    clearReported() {
        var selected = this.state.reported;
        for (var i = 0; i < 9; i++) {
            selected[i].users = [];
        }
        this.setState({
            reported: selected
        });
    }

    update() {
        if (this.organization && this.organization.id !== this.state.orgId) {
            this.setState({
                orgId: this.organization.id,
                reportSettings: undefined
            });
        }

        if (this.organization && !this.state.reportSettings) {
            this.report9BoxDataService.getConfig(this.organization).then((settings: Report9BoxSettings) => {
                this.setState({
                                  reportSettings: settings
                              });
            });
        }

        if (this.teamId !== this.state.teamId) {
            this.setState(
                {
                    teamId: this.teamId
                }
            );
        }
    }

    async componentWillMount() {
        this.update();
    }

    async componentWillReact() {
        // due to the fact that a change to a filter in the sideNav will change the underlying user array here, 
        // we need to do some checks to keep the report in sync with what the user has selected

        // 1. if the organization id changes, we need to clear out the report
        if (this.organizationId !== this.state.orgId) {
            this.setState({orgId: this.organizationId, snapshots: [], selectedSnapshotId: 0});
            await this.props.FilterStore!.setTeamID(0);
            this.clearReported();
        }

        // 2. we need to check for a symmetric difference of this.user to see if we're getting a new collection
        // and call update data accordingly
        const difference = R.symmetricDifferenceWith((a, b) => a.id === b.id, this.users, this.state.previousUsers);
        if (difference.length > 0) {
            this.setState({previousUsers: this.users});
            this.updateData();
        }
        this.update();
    }

    private updateData() {
        this.clearReported();
        if (this.team || this.teamId !== 0) {
            this.report9BoxDataService.getUserStatesByTeam(this.organizationId, this.teamId).then((userStates: any[]) => {
                userStates.map((data: any) => {
                    var userId = data.userId;
                    var box = data.box || null;

                    if (box != null) {
                        this.putUserIdInBox(userId, box);
                    }
                });
            });
        }
    }

    teamChange(e: any) {
        if (this.props.FilterStore) {
            const newTeamId = Number(e.target.value);

            if (newTeamId !== this.state.teamId) {
                if (newTeamId < 0) {
                    this.props.FilterStore.setTeamID(newTeamId).then(() => {
                        // we also need to load a new snapshot list
                        this.report9BoxDataService.getSnapshots(this.organizationId, newTeamId)
                            .then((data: Report9BoxSnapshot[]) => {
                                // this should always find a snapshot as the current one will have a null snapshot value
                                const currentSnapshot = data.find(x => !x.snapshot);

                                this.setState({snapshots: data, selectedSnapshotId: currentSnapshot!.id});
                            })
                            .catch(() => this.props.notificationService.addError('Error loading snapshots for team.'));

                        this.updateData();
                    });
                } else {
                    const team = this.teams.find((t: Team) => {
                        return t.id === newTeamId;
                    });
                    this.props.FilterStore.setTeam(team).then(() => {
                        // we also need to load a new snapshot list
                        if (newTeamId === 0) {
                            this.setState({snapshots: [], selectedSnapshotId: 0});
                        } else {
                            this.report9BoxDataService.getSnapshots(this.organizationId, newTeamId)
                                .then((data: Report9BoxSnapshot[]) => {
                                    // this should always find a snapshot as the current one will have a null snapshot value
                                    const currentSnapshot = data.find(x => !x.snapshot);

                                    this.setState({snapshots: data, selectedSnapshotId: currentSnapshot!.id});
                                })
                                .catch(() => this.props.notificationService.addError('Error loading snapshots for team.'));
                        }
                    });

                    this.props.FilterStore.teamId = newTeamId;
                }
            }
        }
    }

    userSort(a: User, b: User) {
        if (a.lastName > b.lastName) {
            return 1;
        } else if (a.lastName < b.lastName) {
            return -1;
        }
        if (a.firstName > b.firstName) {
            return 1;
        } else if (a.firstName < b.firstName) {
            return -1;
        }
        return 0;
    }

    byKey(key: string) {
        if (this.state.reportSettings && this.state.reportSettings.report9BoxTexts) {
            var d = this.state.reportSettings.report9BoxTexts.find((t: Report9BoxText) => {
                return t.contentKey === key;
            });
            if (d && d.value.trim().length > 0) {
                return d.value;
            }
        }
        return this.defaultKeyValues[key];
    }

    private PVABadge(user: User, index: number) {
        if (!this.survey || !user.userStyles || !this.surveyVersion) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersion.id === surveyVersionId;
        });

        if (userStyle) {
            return <PVABadge key={`pvabadge-${index}`} style={userStyle.style.name} badge={userStyle.style.name[0]} />;
        }

        if (!userStyle && user.userStyles.length > 0) {
            return <PVABadge key={`pvabadge-${index}`} style={user.userStyles[0].style.name} badge={user.userStyles[0].style.name[0]} />;
        }

        return <PVABadge key={`pvabadge-${index}`} style={''} badge={''} />;
    }
    
    private PVABadgeName(user: User, index: number) {
        var name = user.firstName[0] + user.lastName[0];

        if (!this.survey || !user.userStyles || !this.surveyVersion) {
            return <PVABadge key={`pvabadge-${index}`} style={''} badge={name} />;
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersion.id === surveyVersionId;
        });

        if (userStyle) {
            return <PVABadge key={`pvabadge-${index}`} style={userStyle.style.name} badge={name} />;
        }

        if (!userStyle && user.userStyles.length > 0) {
            return <PVABadge key={`pvabadge-${index}`} style={user.userStyles[0].style.name} badge={name} />;
        }

        return <PVABadge key={`pvabadge-${index}`} style={''} badge={name} />;
    }

    private UserStyleName(user: User, index: number) {
        if (!this.survey || !user.userStyles || !this.surveyVersion) {
            return '';
        }

        var surveyVersionId = this.surveyVersion.id;
        var userStyle = user.userStyles.find((us: UserStyle) => {
            return us.style.surveyVersion.id === surveyVersionId;
        });

        if (userStyle) {
            return userStyle.style.name;
        }

        if (!userStyle && user.userStyles.length > 0) {
            return user.userStyles[0].style.name;
        }

        return '';
    }

    private ActionsButton() {
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-header">Actions</li>
                    {!this.state.listView && <li className="options-entry" onClick={(e) => this.setState({listView: true, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> List View</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report as a list view
                        </div>
                    </li>}
                    {this.state.listView && <li className="options-entry" onClick={(e) => this.setState({listView: false, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-th" /> <span> Grid View</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report as a grid view
                        </div>
                    </li>}
                    <li className="options-header">Grid Size</li>
                    {!this.state.small && <li className="options-entry" onClick={(e) => this.setState({small: true, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-search" /> <span> View Small Report</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report as a smaller version to better fit small screens
                        </div>
                    </li>}
                    {this.state.small && <li className="options-entry" onClick={(e) => this.setState({small: false, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-search" /> <span> View Normal Report</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report at its normal size
                        </div>
                    </li>}
                    <li className="options-header">Export</li>
                    <li className="options-entry" onClick={(e) => this.exportCSV()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Export CSV</span>
                        </div>
                    </li>
                    <li className="options-entry" onClick={(e) => this.exportPDF()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-list-alt" /> <span> Export PDF</span>
                        </div>
                    </li>
                </ul>
        </div>);
    }

    private exportCSV() {
        this.setState({
            miniLoad: true, 
            open: false
        });
        if (this.state.selectedSnapshotId) {
            this.report9BoxDataService.getCSVBySnapshot(this.state.selectedSnapshotId).then(() => {
                this.setState({
                    miniLoad: false
                });
            });
        } else {
            this.report9BoxDataService.getCSVByTeam(this.organizationId, this.teamId).then(() => {
                this.setState({
                    miniLoad: false
                });
            });
        }
    }

    private exportPDF() {
        this.setState({
            miniLoad: true, 
            open: false
        });
        if (this.state.selectedSnapshotId) {
            this.report9BoxDataService.getPDFBySnapshot(this.state.selectedSnapshotId).then(() => {
                this.setState({
                    miniLoad: false
                });
            });
        } else {
            this.report9BoxDataService.getPDFByTeam(this.organizationId, this.teamId).then(() => {
                this.setState({
                    miniLoad: false
                });
            });
        }
    }

    private GetKeyOverride(key?: string, fallback?: any) {
        if (key && this.props.FilterStore) {
            return this.props.FilterStore.GetKeyOverride(key, fallback || '');
        }
        return fallback;
    }

    public render() {
        if (this.organization === undefined) {
            return <div />;
        }

        let defaultOptions: any[] = [{
            id: 0,
            value: '- Select -'
        }];
        
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllUsers') > -1 ) {
            defaultOptions.push({
                id: -100,
                value: 'All Users'
            });
        }
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllCandidates') > -1 ) {
            defaultOptions.push({
                id: -200,
                value: 'All Candidates'
            });
        }
        if (!this.props.UserStore!.userRoleIn(['manager', 'user']) || this.userPermissions.indexOf('viewAllFormerEmployees') > -1 ) {
            defaultOptions.push({
                id: -300,
                value: 'All Former Employees'
            });
        }

        const snapshotOptions = this.state.snapshots.map((x: any) => {
            return {
                id: x.id,
                label: x.snapshot ? moment(x.snapshot).format('dddd, MMMM Do YYYY, h:mm:ss a') : 'Current'
            };
        });

        return (
            <div>
                {this.team && <div id={`team-${this.team.id}`} />}
                {this.organization && <div id={`organization-${this.organization.id}`} />}
                
                <PVAContentHeader>
                        <div className="row">
                            <div className="col-xs-7">
                                <div className="title">
                                    {this.GetKeyOverride('9Box', <span>PVA 9-Box<span className="trademark">TM</span></span>)}
                                    <span className="light"> for </span>
                                    <PVATeamSelect defaultOptions={defaultOptions} options={this.teams} selected={this.teamId} onChange={(e: any) => { this.teamChange(e); }} />   
                                    {this.state.miniLoad && <LoadingIndicatorMini />}
                                </div>
                            </div>
                            <div className="col-xs-5 text-right">
                                {this.ActionsButton()}
                            </div>
                        </div>
                </PVAContentHeader>

                <PVAContentBody>

                    {!this.state.listView && 
                        <div className="report9box-wrapper">
                            <div className={`report9box-wrapper-scale ${this.state.small ? 'small' : ''}`}>
                                <div className="report9box-wrapper-scaled">
                                    <div className="report9box">

                                        <div className="report9box-content">
                                            <h4 />
                                            
                                            <div className="report9box-right-line">
                                                <div className="offset" />
                                                <div className="report9box-header">
                                                    <span className="rotate-180" style={{textTransform: 'uppercase'}}>{this.byKey('yArrow')}</span>
                                                    <div className="arrow-up">
                                                            <div className="inner" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="report9box-left-line">
                                                <div className="offset" />
                                                <div className="report9box-header"><span className="rotate-180">{this.byKey('yScale1')}</span></div>
                                                <div className="report9box-header"><span className="rotate-180">{this.byKey('yScale2')}</span></div>
                                                <div className="report9box-header"><span className="rotate-180">{this.byKey('yScale3')}</span></div>
                                            </div>
                                            <div className="mainReport">
                                                <div className="report9box-line">
                                                    <div className="report9box-header">{this.byKey('xScale1')}</div>
                                                    <div className="report9box-header">{this.byKey('xScale2')}</div>
                                                    <div className="report9box-header">{this.byKey('xScale3')}</div>
                                                </div>
                                                {
                                                    [0, 1, 2].map((row: number) => {
                                                        return (
                                                            <div key={`row-${row}`} className="report9box-line">
                                                                {
                                                                    [0, 1, 2].map((boxNum: number) => {
                                                                        var box = row * 3 + boxNum;
                                                                        var className = 'report9box-box box box' + this.state.reported[box].box;
                                                                        var style = {
                                                                            'backgroundColor': this.defaultColors['box' + this.state.reported[box].box + 'Color']
                                                                        };

                                                                        if (this.state.reportSettings && this.state.reportSettings['box' + this.state.reported[box].box + 'Color']) {
                                                                            style.backgroundColor = this.state.reportSettings['box' + this.state.reported[box].box + 'Color'];
                                                                        }

                                                                        return (
                                                                            <div key={`box-${box}`} className={className} onDragOver={this.preventDefault} onDrop={(e) => this.drop(e, this.state.reported[box].box)}>
                                                                                <div className="box-bg">
                                                                                    <div className="box-bg-inner-content">
                                                                                        {this.state.reported[box].box}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="box-header" onClick={() => this.toggleBoxModal(box, true)}>
                                                                                    <div className="box-header-title" style={style}>
                                                                                        <h5>{this.state.reported[box].box}) {this.byKey('box' + (this.state.reported[box].box))}</h5>
                                                                                    </div>
                                                                                    <div className="box-header-users" style={style}>
                                                                                        <span className="glyphicon glyphicon-user" /> {this.state.reported[box].users.sort(this.userSort).filter(this.filterUsers.bind(this)).length}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="box-user-list">
                                                                                    <div className="box-user-list-scrollable">
                                                                                        {
                                                                                            this.state.reported[box].users.sort(this.userSort).filter(this.filterUsers.bind(this)).map((u: User, index: number) => {
                                                                                                const tooltip = <Tooltip className="report9box-tooltip" id={`tooltip-${u.id}-${u.firstName}${u.lastName}`}>
                                                                                                    <span className="tooltip-username">
                                                                                                        <strong>{u.firstName} {u.lastName}</strong>
                                                                                                    </span>
                                                                                                    <span className="badge-container">
                                                                                                        {this.PVABadge(u, index)}
                                                                                                    </span>
                                                                                                </Tooltip>;

                                                                                                return (
                                                                                                    <div key={`person-${index}`} className="person" draggable={!this.state.currentlyViewingPastSnapshot} onDragStart={(e) => this.dragStart(e, u)}>
                                                                                                        <OverlayTrigger key={`ot-${u.id}-${u.firstName}${u.lastName}`} animation={''} delay={0} placement="top" overlay={tooltip}>
                                                                                                            <span className="short-content">
                                                                                                                {this.PVABadgeName(u, index)}
                                                                                                            </span>
                                                                                                        </OverlayTrigger>
                                                                                                    </div>);
                                                                                            })
                                                                                        }
                                                                                        </div>
                                                                                    </div>
                                                                                </div>);
                                                                    })
                                                                }
                                                            </div>);
                                                    })
                                                }
                                            </div>
                                            <div className="report9box-header-bottom">
                                                <span style={{textTransform: 'uppercase'}}>{this.byKey('xArrow')}</span>
                                                <div className="arrow-right">
                                                    <div className="inner" />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="report9box-list-content">
                                            { this.state.currentlyViewingPastSnapshot && 
                                                <label>Currently viewing a previous snapshot of PVA 9-Box <span className="trademark">TM</span></label>
                                            }
                                            { !this.state.currentlyViewingPastSnapshot && 
                                                <div>
                                                    <label>Drag individuals onto the PVA 9-Box <span className="trademark">TM</span></label>
                                                    <ul className="list" onDragOver={this.preventDefault} onDrop={(e) => this.dropRemoveUser(e)}>
                                                        {
                                                            this.users.filter(x => !x.userInBox).filter(this.filterUsers.bind(this))
                                                                .sort(this.userSort)
                                                                .map((user: User, index: number) => {
                                                                    return <li key={`available-person-${index}`} className="person pva-badge-small" draggable={true} onDragStart={(e) => this.dragStart(e, user)}>
                                                                        {this.PVABadge(user, index)}
                                                                        <span className="text">{user.lastName}, {user.firstName}</span>
                                                                    </li>;
                                                                })
                                                        }
                                                    </ul>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    {this.state.listView && <div>

                        {this.state.reported.concat().sort((box1: BoxReport, box2: BoxReport) => {
                            if (box1.box > box2.box) {
                                return 1;
                            } else if (box1.box < box2.box) {
                                return -1;
                            }
                            return 0;
                        }).map((box: BoxReport, ind: number) => {
                            return <div key={`list-view-${ind}`}>
                                <h4>{box.box}. {this.byKey('box' + box.box)}</h4>
                                <div dangerouslySetInnerHTML={{__html: this.byKey('box' + (box.box) + 'Desc')}} />
                                <table className="table table-striped">
                                    <tbody>
                                        {
                                            this.state.reported[ind].users.sort(this.userSort).filter(this.filterUsers.bind(this)).map((u: User, index: number) => {
                                                return (
                                                    <tr key={`modal-${ind}-person-${index}`}>
                                                        <td>
                                                            {u.lastName}, {u.firstName}
                                                        </td>
                                                        <td>
                                                            {this.UserStyleName(u, index)}
                                                        </td>
                                                    </tr>);
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>;
                        })}
                        </div>}

                    { this.teamId !== 0 &&
                        <div className="report-form">
                            <h3>Snapshots</h3>
                            <h4>Load</h4>
                            <PVAGenericSelect options={snapshotOptions} selected={this.state.selectedSnapshotId} ignoreTopPosition={true} onChange={(e: any) => { this.loadSnapshot(e); }} />

                            <h4>Save New</h4>
                            <div className="row">
                                <div className="col-xs-8">
                                    <div className="form-group">
                                        <input className="form-control" type="text" disabled={true} value={moment().format('dddd, MMMM Do YYYY, h:mm:ss a')} />
                                    </div>
                                </div>
                                <div className="col-xs-4">
                                    <button className="btn btn-primary btn-block" disabled={this.state.currentlyViewingPastSnapshot} onClick={(e) => this.snapshot()}>Save Snapshot of Current</button>
                                </div>
                            </div>
                        </div>
                    }

                    {this.state.reported.map((box: BoxReport, ind: number) => {
                        var content = <div>
                            <h4>{box.box}. {this.byKey('box' + box.box)}</h4>
                            <div dangerouslySetInnerHTML={{__html: this.byKey('box' + (box.box) + 'Desc')}} />
                            <table className="table table-striped">
                                <tbody>
                                    {
                                        this.state.reported[ind].users.sort(this.userSort).filter(this.filterUsers.bind(this)).map((u: User, index: number) => {
                                            return (
                                                <tr key={`modal-${ind}-person-${index}`}>
                                                    <td>
                                                        {u.lastName}, {u.firstName}
                                                    </td>
                                                    <td>
                                                        {this.UserStyleName(u, index)}
                                                    </td>
                                                    <td>
                                                        <NavLink className="btn btn-default" to={`/myreport?uid=${u.id}`} target="_blank">View Report</NavLink>
                                                    </td>
                                                </tr>);
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>;
                        return <PonderaModal key={`box-modal-${ind}`} title={'PVA 9-Box <span class="trademark">TM</span>'} content={' '} complexContent={content} onHide={() => this.toggleBoxModal(ind, false)} show={box.show} />;
                    })}

                </PVAContentBody>
            </div>);
    }

    private toggleBoxModal(ind: number, show: boolean) {
        this.state.reported[ind].show = show;
        this.setState(
            {
                reported: this.state.reported
            }, 
            this.forceUpdate);
    }

    private putUserIdInBox(userId: number, box?: number, shouldSave: boolean = false) {
        var user = this.users.find(x => x.id === userId);

        if (user !== undefined) {
            this.putUserInBox(user, box, shouldSave);
        } else {
            // try to find in the other boxes
            for (var i = 0; i < 9; i++) {
                user = this.state.reported[i].users.find(x => x.id === userId);
                if (user !== undefined) {
                    this.putUserInBox(user, box, shouldSave);
                    return;
                }
            }
        }
    }

    private putUserInBox(user: User, box?: number, shouldSave: boolean = false) {
        var reported = this.state.reported;
        var users = this.users;

        // Figure out the location and index of the user

        // Get the users index in the Global List
        var userListInd = this.users.findIndex(x => x.id === user.id);

        // If the user is in a box let's figure out which one
        var userCurrentReportBox = -1;
        var userBoxIndex = -1;
        if (user.userInBox) {
            for (var i = 0; i < 9; i++) {
                userBoxIndex = reported[i].users.findIndex(x => x.id === user.id);
                if (userBoxIndex !== -1) {
                    userCurrentReportBox = i;
                    break;
                }
            }
        }

        if (box !== undefined) {
            // Requesting to put the user in a box
            var boxInd = reported.findIndex(x => x.box === box);

            // insert into the new box
            reported[boxInd].users.push(user);

            // remove from the current box
            if (userCurrentReportBox !== -1) {
                reported[userCurrentReportBox].users.splice(userBoxIndex, 1);
            } else {
                // users.splice(userInd, 1);
                users[userListInd].userInBox = true;
            }
        } else {
            // Requesting to put the user in the global list

            // Splice it out of the current users box
            reported[userCurrentReportBox].users.splice(userBoxIndex, 1);
            // users.push(user);
            users[userListInd].userInBox = false;
        }

        if (!this.state.currentlyViewingPastSnapshot && shouldSave) {
            // Tell the server about this update
            this.report9BoxDataService.updateUser(
                {
                    user: user,
                    box: box
                }, 
                this.organizationId,
                this.teamId
            );
        }

        // Make sure Reacts state is up to date
        this.setState({
            reported: reported
        });
    }

    private dragStart(event: any, user: User) {
        if (!this.canEdit()) {
            return;
        }
        event.dataTransfer.setData('text', JSON.stringify(user.id));
    }

    private preventDefault(event: any) {
        event.preventDefault();
    }

    private canEdit() {
        return this.props.UserStore!.userRoleIn(['superAdmin', 'siteAdmin', 'ponderaAssociate'])
            || this.userPermissions.indexOf('edit9Box') > -1;
    }

    private drop(event: any, ind: number) {
        event.preventDefault();

        var data: any;

        try {
            data = JSON.parse(event.dataTransfer.getData('text'));
        } catch (e) {
            // If the text data isn't parsable we'll just ignore it.
            return;
        }

        this.putUserIdInBox(data, ind, true);
    }

    private dropRemoveUser(event: any) {

        event.preventDefault();

        var data: any;

        try {
            data = JSON.parse(event.dataTransfer.getData('text'));
        } catch (e) {
            // If the text data isn't parsable we'll just ignore it.
            return;
        }

        this.putUserIdInBox(data, undefined, true);
    }

    private filterUsers(user: User) {
        var f = this.state.filter.toLowerCase();
        if (user.firstName.toLowerCase().indexOf(f) > -1) { return true; }
        return false;
    }

    private snapshot() {
        this.report9BoxDataService.snapshot(this.organizationId, this.teamId)
            .then(() => this.props.notificationService.addNotification('Snapshot saved successfully'))
            .catch(() => this.props.notificationService.addError('Error saving snapshot. Please try again.'));
    }

    private loadSnapshot(e: any) {
        const selectedSnapshot = this.state.snapshots.find(snapshot => snapshot.id === e.target.value);

        // always set currently viewing past snapshot to true here. It will save on network requests
        // in the map below.
        this.setState({selectedSnapshotId: e.target.value, currentlyViewingPastSnapshot: true});

        this.report9BoxDataService.getUserStatesBySnapshot(e.target.value)
            .then((userStates: any[]) => {
                this.clearReported();
                userStates.map((data: any) => {
                    var userId = data.userId;
                    var box = data.box || null;

                    if (box != null) {
                        this.putUserIdInBox(userId, box);
                    }
                });

                if (selectedSnapshot && !selectedSnapshot.snapshot) {
                    // this is the current snapshot entry
                    this.setState({currentlyViewingPastSnapshot: false});
                }
            })
            .catch(() => this.props.notificationService.addError('Error loading snapshot. Please try again.'));
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/9box/9boxView.tsx